import { Component, Input, Output, EventEmitter, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  ParkingTypeDto,
  ParkingTypeService,
  DefaultExemptionDto,
  CustomExemptionDto,
  AssignedZoneParkersDto,
  PoolParkingDto,
  LongTermParkersDto,
  ShortTermParkingDto,
  EasypassDto
} from '../../../../api/gen';
import { environment } from '../../../../environments/environment';
import { JJamesBadgeComponent, BadgeVariant } from 'shared-lib';

// Union type for all parking type DTOs
export type ParkingTypeUnion = DefaultExemptionDto | CustomExemptionDto | AssignedZoneParkersDto | PoolParkingDto | LongTermParkersDto | ShortTermParkingDto | EasypassDto;
export type TypeCategory = 'default-exemption' | 'custom-exemption' | 'assigned-zone-parkers' | 'pool-parking' | 'long-term' | 'short-term' | 'easypass';

@Component({
  selector: 'app-parking-type-item',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    JJamesBadgeComponent
  ],
  templateUrl: './parking-type-item.component.html',
  styleUrl: './parking-type-item.component.scss'
})
export class ParkingTypeItemComponent {
  private parkingTypeService = inject(ParkingTypeService);
  private snackBar = inject(MatSnackBar);

  @Input({ required: true }) parkingTypeItem!: ParkingTypeUnion;
  @Input({ required: true }) parkingSpaceId!: string;
  @Input({ required: true }) typeCategory!: TypeCategory;

  @Output() deleted = new EventEmitter<ParkingTypeUnion>();

  isDeleting = signal(false);

  // Get the nested ParkingTypeDto
  get parkingType(): ParkingTypeDto | undefined {
    return this.parkingTypeItem.parkingType;
  }

  getTypeName(): string {
    return this.parkingType?.type?.name || 'Unknown Type';
  }

  getTypeDescription(): string {
    return this.parkingType?.type?.description || '';
  }

  getTypeEnum(): string {
    return this.parkingType?.type?.type || 'Default';
  }

  // Get type category display name
  getTypeCategoryName(): string {
    switch (this.typeCategory) {
      case 'default-exemption':
        return 'Default Exemption';
      case 'custom-exemption':
        return 'Custom Exemption';
      case 'assigned-zone-parkers':
        return 'Assigned Zone Parkers';
      case 'pool-parking':
        return 'Pool Parking';
      case 'long-term':
        return 'Long-Term';
      case 'short-term':
        return 'Short-Term';
      case 'easypass':
        return 'Easypass';
      default:
        return 'Unknown';
    }
  }

  getTypeBadgeVariant(): BadgeVariant {
    switch (this.typeCategory) {
      case 'default-exemption':
        return 'warning';
      case 'custom-exemption':
        return 'danger';
      case 'assigned-zone-parkers':
        return 'success';
      case 'pool-parking':
        return 'primary';
      case 'long-term':
        return 'info';
      case 'short-term':
        return 'secondary';
      case 'easypass':
        return 'warning';
      default:
        return 'primary';
    }
  }

  // Get additional info based on type
  getAdditionalInfo(): string {
    if (this.typeCategory === 'default-exemption' && 'licensePlateRegex' in this.parkingTypeItem) {
      const regex = (this.parkingTypeItem as DefaultExemptionDto).licensePlateRegex;
      return regex ? `Regex: ${regex}` : '';
    }
    if (this.typeCategory === 'custom-exemption' && 'exemptionLicensePlates' in this.parkingTypeItem) {
      const plates = (this.parkingTypeItem as CustomExemptionDto).exemptionLicensePlates;
      return plates ? `${plates.length} license plate(s)` : '';
    }
    if (this.typeCategory === 'pool-parking') {
      const poolItem = this.parkingTypeItem as PoolParkingDto;
      return poolItem.parkingTypeId ? `Type ID: ${poolItem.parkingTypeId.substring(0, 8)}...` : '';
    }
    return '';
  }

  getOperatorName(): string {
    const op = this.parkingType?.operator;
    if (!op) return '';
    return [op.firstName, op.lastName].filter(Boolean).join(' ').trim();
  }

  getOrganizationName(): string {
    return this.parkingType?.organization?.name || '';
  }

  /** Resolved cost-center name — survives the underlying OrgCostCenter being later soft-deleted. */
  getCostCenterName(): string {
    if (this.typeCategory !== 'easypass') return '';
    const easypass = this.parkingTypeItem as EasypassDto;
    return easypass.costCenterName || '';
  }

  /** Direct link to this invitation's redeem page in the parker app, or '' when it has no token yet. */
  getRedeemLink(): string {
    if (this.typeCategory !== 'easypass') return '';
    const token = (this.parkingTypeItem as EasypassDto).token;
    return token ? `${environment.parkerUrl}/easypass/redeem/${token}` : '';
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  }

  getAvailabilityText(): string {
    const from = this.parkingType?.availableFrom;
    const to = this.parkingType?.availableTo;

    if (!from && !to) {
      return 'Always available';
    }

    if (from && to) {
      return `${this.formatDate(from)} - ${this.formatDate(to)}`;
    }

    if (from) {
      return `From ${this.formatDate(from)}`;
    }

    return `Until ${this.formatDate(to)}`;
  }

  onDelete(): void {
    const confirmed = confirm(`Are you sure you want to remove "${this.getTypeName()}" from this parking space?`);

    if (confirmed) {
      this.performDelete();
    }
  }

  private performDelete(): void {
    if (!this.parkingTypeItem.id) return;

    this.isDeleting.set(true);

    // Emit the delete event - parent component handles the actual API call
    this.deleted.emit(this.parkingTypeItem);
    this.isDeleting.set(false);
  }
}
